import { Customer, Device, Registration, User, xhr as sdkXhr } from "coolremote-sdk";
import { t } from "ttag";
import { markPublicRegistrationTokenSet } from "../publicRegistrationSession";

export interface LoginResult {
  userId: string;
  username: string;
  token: string;
  hasProfessionalAccess: boolean;
}

export interface TwoFactorRequired {
  is2FA: true;
}

export interface CustomerSummary {
  customerId: string;
  name: string;
}

export interface SiteSummary {
  siteId: string;
  name: string;
}

/**
 * Real service calls for the public (pre-login) existing-user registration
 * flow. Uses `User.connect` for login, and the standard authenticated
 * `Customer` endpoints for the rest — the SDK's in-memory token is enough to
 * authenticate them for the rest of this wizard.
 *
 * Deliberately NOT persisted to `localStorage` and NOT reflected in the
 * app's global `isLoggedIn` store flag — either one would make `Routes.tsx`
 * run its normal post-login bootstrap (fetch the user's full tree, show a
 * blocking full-screen loader) on top of this still-mid-wizard screen. The
 * technician is expected to log in for real through the normal login screen
 * once the wizard finishes (see `CongratulationsStep`).
 */
// `userId` here is only ever used as an "is authenticated" guard downstream
// (never sent to an API) — `data.id` isn't a field these endpoints reliably
// return, so use the already-validated `username` instead.
const buildLoginResult = (
  username: string,
  data: { token: string; hasProfessionalAccess?: boolean }
): LoginResult => {
  sdkXhr.setToken(data.token);
  markPublicRegistrationTokenSet();

  return {
    userId: username,
    username,
    token: data.token,
    // A Control-only account has no Professional access, so
    // `Customer.getMyCustomers`/`getCustomerSites` below are off-limits to it
    // — SelectCustomerStep uses this flag to skip straight to creating a new
    // customer instead of showing an empty customers list.
    hasProfessionalAccess: !!data.hasProfessionalAccess
  };
};

export const login = async (
  username: string,
  password: string
): Promise<LoginResult | TwoFactorRequired> => {
  const { data, message } = await User.connect(username, password, "CoolCommercial");

  if (message) {
    throw new Error(message);
  }

  if (data?.is2FA) {
    // Same handshake as the app's normal login (`RootStore.ts`'s `doLogin`):
    // a 2FA-required account comes back as a success with a short-lived
    // `token2FA` instead of the real `token` — that temporary token must be
    // set before `verifyTwoFactorCode` can call the verify endpoint.
    if (data.token2FA) {
      sdkXhr.setToken(data.token2FA);
      markPublicRegistrationTokenSet();
    }
    return { is2FA: true };
  }

  if (!data?.token) {
    throw new Error(t`Incorrect username or password`);
  }

  return buildLoginResult(username, data);
};

export const verifyTwoFactorCode = async (username: string, code: string): Promise<LoginResult> => {
  const { data, message } = await User.verify2FA(code, "CoolCommercial");

  if (message || !data?.token) {
    throw new Error(message || t`The code you entered is invalid or expired`);
  }

  return buildLoginResult(username, data);
};

// Both endpoints return an object map keyed by id (same convention as
// `Customer.getCustomers` → `ICustomerMap` and `getCustomerSites` elsewhere
// in the app, e.g. RegisterNewDevice.tsx's `Object.values(sites)`), not a
// plain array.
export const listCustomers = (): Promise<CustomerSummary[]> =>
  Customer.getMyCustomers().then((customers: Record<string, { id: string; name: string }>) =>
    Object.values(customers).map((customer) => ({ customerId: customer.id, name: customer.name }))
  );

export const listSites = (customerId: string): Promise<SiteSummary[]> =>
  Customer.getCustomerSites(customerId).then((sites: Record<string, { id: string; name: string }>) =>
    Object.values(sites).map((site) => ({ siteId: site.id, name: site.name }))
  );

export const createCustomerFromDevice = (
  serial: string,
  pin: string,
  customerName: string
): Promise<CustomerSummary> =>
  Registration.createCustomerFromDevice({ serial, pin, customerName }).then(
    (customer: { id: string; name: string }) => ({ customerId: customer.id, name: customer.name })
  );

/**
 * Associates the device with the chosen site — same call the authenticated
 * "Add Device" screen uses (`Device.create` via `POST /devices`, `site` in
 * the payload rather than a nested `/sites/:id/devices` route).
 */
export const associateDevice = (
  siteId: string,
  serial: string,
  pin: string,
  description: string
): Promise<{ deviceId: string }> =>
  Device.create({ site: siteId, serial, pin, description }).then((device: { id: string }) => ({
    deviceId: device.id
  }));
